import { useEffect, useRef } from "react";
import { confusionLabel } from "@xinghai/shared";
import { ZHE_NAMES } from "@xinghai/shared";
import type { Domain, MarkType, Zhe } from "@xinghai/shared";
import { Button, Field, Icon, Input, SegmentedControl, Select, Slider } from "@xinghai/ui";
import { AudioPanel } from "./AudioPanel";
import type { CharDimKey } from "../labels";
import {
  CHAR_DIMS,
  DIMENSION_LABEL,
  FINAL_OPTIONS,
  INITIAL_OPTIONS,
  MARK_LABEL,
  MARK_ORDER,
  TONE_OPTIONS,
} from "../labels";
import { applyMarkDefaults, applyOmission } from "../draft";
import type { CharDraft, WorkDraft } from "../draft";
import type { AnnotateMaterialChar } from "../types";

export interface SingleCharWorkbenchProps {
  audioUrl: string;
  char: AnnotateMaterialChar;
  /** 红线 14.1 — singing shows no tone mark. */
  pinyinDisplay: string;
  domain: Domain;
  value: CharDraft;
  onChange: (next: CharDraft) => void;
  draft: WorkDraft;
  onDraftChange: (next: WorkDraft) => void;
  /** Detail panel is open (「有问题」 was chosen). */
  expanded: boolean;
  onExpandedChange: (open: boolean) => void;
  submitting: boolean;
  /** `nextChar` short-circuits React state so 「正确」 is a single click. */
  onSubmit: (nextChar?: CharDraft) => void;
  disabled?: boolean;
  issues?: string[];
}

function inTextEntry(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  const tag = el?.tagName?.toLowerCase();
  return (
    tag === "input" || tag === "textarea" || tag === "select" || el?.isContentEditable === true
  );
}

/**
 * docs/UX.md §2.2 — 单字工作台: one decision per screen.
 *
 * Keyboard (the doc's map): Space 播放/暂停 · R 重播 (both owned by AudioPanel)
 * · Enter 正确 · X 有问题 · 1–6 错误类型 · Esc 收起.
 */
export function SingleCharWorkbench({
  audioUrl,
  char,
  pinyinDisplay,
  domain,
  value,
  onChange,
  draft,
  onDraftChange,
  expanded,
  onExpandedChange,
  submitting,
  onSubmit,
  disabled = false,
  issues,
}: SingleCharWorkbenchProps) {
  const dims = CHAR_DIMS[domain];
  const singing = domain === "singing";
  const detailRef = useRef<HTMLDivElement>(null);

  const markCorrect = () => {
    const next = applyMarkDefaults(value, domain, "correct");
    onChange(next);
    onExpandedChange(false);
    onSubmit(next);
  };

  const markProblem = () => {
    onExpandedChange(true);
    if (value.mark_type === null || value.mark_type === "correct") {
      onChange(applyMarkDefaults(value, domain, "substitution"));
    }
  };

  const pickMark = (m: MarkType) => onChange(applyMarkDefaults(value, domain, m));

  useEffect(() => {
    if (disabled || typeof document === "undefined") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (inTextEntry(e.target)) return;
      if (e.key === "Enter") {
        e.preventDefault();
        if (expanded) onSubmit(undefined);
        else markCorrect();
      } else if (e.key === "x" || e.key === "X") {
        e.preventDefault();
        markProblem();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onExpandedChange(false);
      } else if (expanded && /^[1-6]$/.test(e.key)) {
        const m = MARK_ORDER[Number(e.key) - 1];
        if (!m) return;
        e.preventDefault();
        pickMark(m);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    if (expanded) detailRef.current?.focus();
  }, [expanded]);

  const showRealized = value.mark_type === "substitution";
  const showOmission = value.mark_type === "deletion";
  const showRealizedTone = !singing && value.tone !== null && value.tone < 1;

  return (
    <div className="xz-single">
      <aside className="xz-single__audio">
        <AudioPanel src={audioUrl} autoPlay label={`录音 ${char.char}`} />
      </aside>

      <main className="xz-single__stage">
        <p className="xz-single__char" lang="zh-CN">
          {char.char}
        </p>
        <p className="xz-single__pinyin">{pinyinDisplay}</p>
        {char.confusion_risk.length > 0 && (
          <p className="gz-caption">易混：{char.confusion_risk.map(confusionLabel).join("、")}</p>
        )}
      </main>

      <aside className="xz-single__decide">
        <Button
          variant="primary"
          size="lg"
          block
          disabled={disabled}
          loading={submitting && !expanded}
          onClick={markCorrect}
        >
          <Icon id="ui-check" size={20} /> 正确（Enter）
        </Button>
        <Button variant="secondary" size="lg" block disabled={disabled} onClick={markProblem}>
          <Icon id="ui-close" size={20} /> 有问题（X）
        </Button>

        {expanded && (
          <div className="xz-single__detail" ref={detailRef} tabIndex={-1} aria-label="细评">
            <SegmentedControl<MarkType>
              size="sm"
              label="错误类型"
              options={MARK_ORDER.map((m, i) => ({
                value: m,
                label: MARK_LABEL[m],
                title: `${MARK_LABEL[m]}（快捷键 ${i + 1}）`,
              }))}
              value={value.mark_type}
              disabled={disabled}
              onChange={pickMark}
            />

            {showOmission && (
              <SegmentedControl<CharDimKey>
                size="sm"
                label="省略的是哪一部分"
                options={dims.map((d) => ({ value: d, label: DIMENSION_LABEL[d] ?? d }))}
                value={
                  dims.find(
                    (d) => (value as unknown as Record<CharDimKey, number | null>)[d] === 0,
                  ) ?? null
                }
                disabled={disabled}
                onChange={(d) => onChange(applyOmission(value, d))}
              />
            )}

            {showRealized && (
              <Field label="实现音素">
                <Select
                  value={value.realized ?? ""}
                  disabled={disabled}
                  onChange={(e) =>
                    onChange({ ...value, realized: e.target.value || null, touched: true })
                  }
                >
                  <option value="">未选择</option>
                  <optgroup label="声母">
                    {INITIAL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="韵母">
                    {FINAL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </optgroup>
                </Select>
              </Field>
            )}

            {showRealizedTone && (
              <Field label="实现调类">
                <SegmentedControl<number>
                  size="sm"
                  label="实现调类"
                  options={TONE_OPTIONS.map((t) => ({ value: t.value, label: t.label }))}
                  value={value.realized_tone}
                  disabled={disabled}
                  onChange={(t) => onChange({ ...value, realized_tone: t, touched: true })}
                />
              </Field>
            )}

            {singing && (
              <Field label="辙位">
                <Select
                  value={value.zhe_realized ?? ""}
                  disabled={disabled}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      zhe_realized: (e.target.value || null) as Zhe | null,
                      touched: true,
                    })
                  }
                >
                  <option value="">未选择</option>
                  {ZHE_NAMES.map((z) => (
                    <option key={z} value={z}>
                      {z}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <div className="xz-single__sliders">
              {dims.map((dim) => (
                <label className="xz-charjudge__dim" key={dim}>
                  <span className="xz-charjudge__dimname">{DIMENSION_LABEL[dim]}</span>
                  <Slider
                    value={(value as unknown as Record<CharDimKey, number | null>)[dim]}
                    step={0.05}
                    label={`${char.char} ${DIMENSION_LABEL[dim]}`}
                    disabled={disabled}
                    onChange={(n) => {
                      const next: CharDraft = { ...value, touched: true };
                      (next as unknown as Record<CharDimKey, number | null>)[dim] = n;
                      onChange(next);
                    }}
                  />
                </label>
              ))}
            </div>

            <Field label="备注" hideLabel>
              <Input
                value={value.note ?? ""}
                placeholder="备注（可选）"
                aria-label="备注"
                disabled={disabled}
                onChange={(e) =>
                  onChange({ ...value, note: e.target.value || null, touched: true })
                }
              />
            </Field>

            <Button
              variant="primary"
              size="lg"
              block
              loading={submitting}
              disabled={disabled}
              onClick={() => onSubmit(undefined)}
            >
              提交（Enter）
            </Button>
          </div>
        )}

        {/* 单字任务: 费力度 / 口音度 are rarely needed — fold them away. */}
        <details className="xz-single__more">
          <summary>更多</summary>
          <Field label="费力度" hint="听懂这条要花多少力气。">
            <Slider
              value={draft.listening_effort}
              label="费力度"
              step={0.05}
              disabled={disabled}
              onChange={(v) => onDraftChange({ ...draft, listening_effort: v })}
            />
          </Field>
          <Field label="口音度" hint="口音度不计入评分，只单列记录。">
            <Slider
              value={draft.accentedness}
              label="口音度"
              step={0.05}
              disabled={disabled}
              onChange={(v) => onDraftChange({ ...draft, accentedness: v })}
            />
          </Field>
        </details>

        {issues && issues.length > 0 && (
          <div className="gz-notice gz-notice--error" role="alert">
            <ul className="xz-utterance__issues">
              {issues.map((i) => (
                <li key={i}>{i}</li>
              ))}
            </ul>
          </div>
        )}

        <p className="gz-caption">
          空格 播放/暂停 · R 重播 · Enter 正确 · X 有问题 · 1–6 类型 · Esc 收起
        </p>
      </aside>
    </div>
  );
}
